import { useEffect, useRef, useState } from 'react';

/**
 * Searchable multi-select over a fixed option list. Selected values render as
 * removable chips; typing filters the remaining options. The value is a
 * comma-separated string — the wire format the service_areas / languages fields
 * already use — so it drops into existing forms without model changes.
 */
export default function SearchableMultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  emptyText = 'No matches',
  id,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const selected = String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const set = (next) => onChange([...new Set(next)].join(','));
  const add = (opt) => {
    set([...selected, opt]);
    setQuery('');
  };
  const remove = (opt) => set(selected.filter((s) => s !== opt));

  const matches = options
    .filter((o) => !selected.includes(o))
    .filter((o) => o.toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 8);

  return (
    <div className="searchable-select" ref={boxRef}>
      {selected.length > 0 && (
        <div className="chips" style={{ marginBottom: 8 }}>
          {selected.map((s) => (
            <span key={s} className="chip active">
              {s}
              <button
                type="button"
                className="chip-remove"
                aria-label={`Remove ${s}`}
                onClick={() => remove(s)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="searchable-input">
        <input
          id={id}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
        />
        {open && (
          <ul className="searchable-menu" role="listbox">
            {matches.length === 0 ? (
              <li className="searchable-empty">{query ? emptyText : 'Type to search'}</li>
            ) : (
              matches.map((o) => (
                <li key={o}>
                  <button type="button" onClick={() => add(o)}>
                    {o}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
